import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft } from "lucide-react";

const registerSchema = z.object({
  email: z.string().email("Ongeldig e-mailadres"),
  naam: z.string().min(2, "Naam moet minimaal 2 karakters bevatten"),
  bedrijf: z.enum(["Luijten VVZ", "Decoprof"]),
  telefoon: z.string().min(6, "Telefoonnummer moet minimaal 6 cijfers bevatten"),
  wachtwoord: z.string().min(6, "Wachtwoord moet minimaal 6 karakters bevatten"),
  wachtwoord_bevestig: z.string(),
}).refine((data) => data.wachtwoord === data.wachtwoord_bevestig, {
  message: "Wachtwoorden komen niet overeen",
  path: ["wachtwoord_bevestig"],
});

type RegisterFormData = z.infer<typeof registerSchema>;

const Register = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Pre-fill naam from URL params (when redirected from legacy login)
  const urlParams = new URLSearchParams(window.location.search);
  const prefillNaam = urlParams.get('naam') || '';

  const { register, handleSubmit, formState: { errors }, setValue } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      bedrijf: "Luijten VVZ",
      naam: prefillNaam,
    }
  });

  const onSubmit = async (data: RegisterFormData) => {
    setIsSubmitting(true);
    
    try {
      // Hash password server-side via RPC
      const { data: hashedPassword, error: hashError } = await supabase.rpc(
        "hash_password_for_registration",
        { plain_password: data.wachtwoord }
      );
      if (hashError) throw hashError;

      // Match medewerker by name
      let matchedMedewerkerId: string | null = null;
      try {
        const { data: matchResult } = await supabase.rpc("match_medewerker_by_name", {
          input_naam: data.naam,
        });
        matchedMedewerkerId = matchResult || null;
      } catch {
        // Matching is optional, don't block registration
      }

      const { error } = await supabase
        .from("registration_requests")
        .insert({
          email: data.email,
          naam: data.naam,
          bedrijf: data.bedrijf,
          telefoon: data.telefoon,
          wachtwoord_hash: hashedPassword,
          matched_medewerker_id: matchedMedewerkerId,
          requested_role: "medewerker",
          status: "pending"
        });

      if (error) throw error;

      toast.success("Registratie aanvraag verzonden!", {
        description: "Je ontvangt een e-mail zodra je aanvraag is goedgekeurd."
      });
      
      navigate("/login");
    } catch (error: any) {
      console.error("Registration error:", error);
      
      if (error.code === "23505") {
        toast.error("Dit e-mailadres is al geregistreerd");
      } else {
        toast.error("Registratie mislukt", {
          description: error.message
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-luijten-gray p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/login")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <CardTitle>Registreren</CardTitle>
          </div>
          <CardDescription>
            Vraag toegang aan tot het systeem. Een beheerder moet je aanvraag goedkeuren.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="naam">Volledige naam *</Label>
              <Input
                id="naam"
                placeholder="Jan Janssen"
                {...register("naam")}
              />
              {errors.naam && (
                <p className="text-sm text-destructive">{errors.naam.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres *</Label>
              <Input
                id="email"
                type="email"
                placeholder="naam@bedrijf.nl"
                {...register("email")}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bedrijf">Bedrijf *</Label>
              <Select
                defaultValue="Luijten VVZ"
                onValueChange={(value) => setValue("bedrijf", value as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Luijten VVZ">Luijten VVZ</SelectItem>
                  <SelectItem value="Decoprof">Decoprof</SelectItem>
                </SelectContent>
              </Select>
              {errors.bedrijf && (
                <p className="text-sm text-destructive">{errors.bedrijf.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="telefoon">Telefoonnummer *</Label>
              <Input
                id="telefoon"
                type="tel"
                placeholder="06-12345678"
                {...register("telefoon")}
              />
              {errors.telefoon && (
                <p className="text-sm text-destructive">{errors.telefoon.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="wachtwoord">Wachtwoord *</Label>
              <Input
                id="wachtwoord"
                type="password"
                placeholder="Minimaal 6 karakters"
                {...register("wachtwoord")}
              />
              {errors.wachtwoord && (
                <p className="text-sm text-destructive">{errors.wachtwoord.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="wachtwoord_bevestig">Wachtwoord bevestigen *</Label>
              <Input
                id="wachtwoord_bevestig"
                type="password"
                placeholder="Herhaal je wachtwoord"
                {...register("wachtwoord_bevestig")}
              />
              {errors.wachtwoord_bevestig && (
                <p className="text-sm text-destructive">{errors.wachtwoord_bevestig.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Aanvraag verzenden..." : "Aanvraag indienen"}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Heb je al een account?{" "}
              <Button
                variant="link"
                className="p-0 h-auto"
                onClick={() => navigate("/login")}
              >
                Inloggen
              </Button>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
